/**
 * Pure helpers for the sponsor console (NOS-164): portfolio aggregation over
 * RLS-scoped donation rows and CSV export. Kept free of Supabase/React so the
 * math is unit-testable.
 */
import { escapeCSVField, downloadCsv } from "@/lib/csv"
import { formatCurrency } from "@/lib/format"

// NOS-263: canonical implementations moved to lib/csv.ts; re-exported here
// for existing importers.
export { escapeCSVField, downloadCsv }

export interface SponsoredChildOrg {
  id: string
  name: string
  slug: string
  logo_url: string | null
}

export interface SponsorshipWithChild {
  id: string
  child_org_id: string
  mode: "fiscal" | "affiliated"
  status: "invited" | "active" | "transferring" | "exited"
  admin_fee_pct: number
  tags: string[]
  started_at: string | null
  child: SponsoredChildOrg
}

export interface SponsorDonationRow {
  org_id: string
  amount: number
  created_at: string
  status: string
  donor_name: string | null
  donor_email: string | null
  campaign_id: string | null
}

export interface ChildDonationStats {
  totalCents: number
  ytdCents: number
  lastDonationAt: string | null
}

export interface PortfolioRollup {
  totalRaisedCents: number
  raisedThisMonthCents: number
  byOrg: Map<string, ChildDonationStats>
}

export function formatCurrencyCents(cents: number): string {
  return formatCurrency(cents)
}

/**
 * Aggregates succeeded donation rows into portfolio totals and per-child stats.
 * `now` is injectable for deterministic tests; month/YTD boundaries use the
 * viewer's local calendar.
 */
export function aggregatePortfolio(
  donations: Pick<SponsorDonationRow, "org_id" | "amount" | "created_at" | "status">[],
  now: Date = new Date()
): PortfolioRollup {
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const yearStart = new Date(now.getFullYear(), 0, 1)

  let totalRaisedCents = 0
  let raisedThisMonthCents = 0
  const byOrg = new Map<string, ChildDonationStats>()

  for (const d of donations) {
    if (d.status !== "succeeded") continue
    const at = new Date(d.created_at)
    totalRaisedCents += d.amount
    if (at >= monthStart) raisedThisMonthCents += d.amount

    const stats = byOrg.get(d.org_id) ?? {
      totalCents: 0,
      ytdCents: 0,
      lastDonationAt: null,
    }
    stats.totalCents += d.amount
    if (at >= yearStart) stats.ytdCents += d.amount
    if (stats.lastDonationAt === null || at > new Date(stats.lastDonationAt)) {
      stats.lastDonationAt = d.created_at
    }
    byOrg.set(d.org_id, stats)
  }

  return { totalRaisedCents, raisedThisMonthCents, byOrg }
}


/**
 * Cross-org donation CSV for sponsor reporting. One row per donation:
 * child org name, date, donor name/email, amount, status, campaign title.
 */
export function buildSponsorDonationsCsv(
  donations: SponsorDonationRow[],
  orgNamesById: Map<string, string>,
  campaignTitlesById: Map<string, string>
): string {
  const headers = ["Project", "Date", "Donor Name", "Donor Email", "Amount", "Status", "Campaign"]
  const rows = donations.map((d) => [
    escapeCSVField(orgNamesById.get(d.org_id) ?? d.org_id),
    new Date(d.created_at).toISOString().split("T")[0],
    escapeCSVField(d.donor_name ?? ""),
    escapeCSVField(d.donor_email ?? ""),
    (d.amount / 100).toFixed(2),
    escapeCSVField(d.status),
    escapeCSVField(d.campaign_id ? (campaignTitlesById.get(d.campaign_id) ?? "") : ""),
  ])
  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n")
}


export type ProjectSortKey = "name" | "status" | "ytd" | "lastDonation" | "members"

export interface ProjectTableRow {
  sponsorship: SponsorshipWithChild
  ytdCents: number
  totalCents: number
  lastDonationAt: string | null
  memberCount: number
  contactCount: number
  /** Ledger balance for fiscal projects; null for affiliated (no fund). */
  fundBalanceCents?: number | null
  /** Pending disbursement requests awaiting the sponsor's decision. */
  pendingRequests?: number
}

/** Filter by free-text search (child name) and tag; sort by the given column. */
export function filterAndSortProjects(
  rows: ProjectTableRow[],
  search: string,
  tag: string | null,
  sortKey: ProjectSortKey,
  ascending: boolean
): ProjectTableRow[] {
  const q = search.trim().toLowerCase()
  const filtered = rows.filter((r) => {
    if (q && !r.sponsorship.child.name.toLowerCase().includes(q)) return false
    if (tag && !r.sponsorship.tags.includes(tag)) return false
    return true
  })
  const dir = ascending ? 1 : -1
  return [...filtered].sort((a, b) => {
    switch (sortKey) {
      case "name":
        return dir * a.sponsorship.child.name.localeCompare(b.sponsorship.child.name)
      case "status":
        return dir * a.sponsorship.status.localeCompare(b.sponsorship.status)
      case "ytd":
        return dir * (a.ytdCents - b.ytdCents)
      case "members":
        return dir * (a.memberCount - b.memberCount)
      case "lastDonation": {
        const at = a.lastDonationAt ? new Date(a.lastDonationAt).getTime() : 0
        const bt = b.lastDonationAt ? new Date(b.lastDonationAt).getTime() : 0
        return dir * (at - bt)
      }
      default:
        return 0
    }
  })
}
